import React, { createContext, useState, useContext, useEffect } from 'react';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
  signInAnonymously,
  linkWithPopup,
  linkWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../index';

// Create context
const AuthContext = createContext();

// Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAnonymous = user?.isAnonymous ?? true;

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        // No user — auto sign-in anonymously
        try {
          await signInAnonymously(auth);
        } catch (err) {
          console.error('Anonymous sign-in failed:', err);
          setLoading(false);
        }
        return; // onAuthStateChanged will fire again with the anonymous user
      }

      setUser(user);

      if (user.isAnonymous) {
        // Anonymous user — no Firestore user doc
        setUserData(null);
        setLoading(false);
        return;
      }

      // Real user — get additional user data from Firestore
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        } else {
          // If user document doesn't exist, create one
          const newUserData = {
            email: user.email,
            displayName: user.displayName || user.email.split('@')[0],
            photoURL: user.photoURL || null,
            subscription_tier: 'basic',
            subscription_expires: null,
            role: 'consumer',
            created_at: serverTimestamp(),
            last_login: serverTimestamp()
          };

          await setDoc(doc(db, 'users', user.uid), newUserData);
          setUserData(newUserData);
        }
      } catch (error) {
        console.error('Error getting user data:', error);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Sign in with email and password
  const signIn = async (email, password) => {
    setError(null);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Update last login time
      await setDoc(
        doc(db, 'users', userCredential.user.uid),
        { last_login: serverTimestamp() },
        { merge: true }
      );

      return userCredential.user;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);

      // Check if user exists in Firestore
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));

      if (!userDoc.exists()) {
        // Create user document
        const newUserData = {
          email: userCredential.user.email,
          displayName: userCredential.user.displayName || userCredential.user.email.split('@')[0],
          photoURL: userCredential.user.photoURL || null,
          subscription_tier: 'basic',
          subscription_expires: null,
          role: 'consumer',
          created_at: serverTimestamp(),
          last_login: serverTimestamp()
        };

        await setDoc(doc(db, 'users', userCredential.user.uid), newUserData);
      } else {
        // Update last login time
        await setDoc(
          doc(db, 'users', userCredential.user.uid),
          { last_login: serverTimestamp() },
          { merge: true }
        );
      }

      return userCredential.user;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Sign up with email and password
  const signUp = async (email, password, displayName) => {
    setError(null);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Update profile with display name
      if (displayName) {
        await updateProfile(userCredential.user, { displayName });
      }

      // Create user document in Firestore
      const newUserData = {
        email: userCredential.user.email,
        displayName: displayName || userCredential.user.email.split('@')[0],
        photoURL: userCredential.user.photoURL || null,
        subscription_tier: 'basic',
        subscription_expires: null,
        role: 'consumer',
        created_at: serverTimestamp(),
        last_login: serverTimestamp()
      };

      await setDoc(doc(db, 'users', userCredential.user.uid), newUserData);

      return userCredential.user;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Link anonymous account with Google
  const linkWithGoogle = async () => {
    setError(null);
    try {
      if (!user || !user.isAnonymous) {
        return signInWithGoogle();
      }

      const provider = new GoogleAuthProvider();
      const result = await linkWithPopup(user, provider);

      // Create user doc
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      if (!userDoc.exists()) {
        const newUserData = {
          email: result.user.email,
          displayName: result.user.displayName || result.user.email.split('@')[0],
          photoURL: result.user.photoURL || null,
          subscription_tier: 'basic',
          subscription_expires: null,
          role: 'consumer',
          created_at: serverTimestamp(),
          last_login: serverTimestamp()
        };
        await setDoc(doc(db, 'users', result.user.uid), newUserData);
      }

      return result.user;
    } catch (error) {
      if (error.code === 'auth/credential-already-in-use') {
        // Google account already exists — fall back to normal sign-in
        return signInWithGoogle();
      }
      setError(error.message);
      throw error;
    }
  };

  // Link anonymous account with email/password
  const linkWithEmail = async (email, password, displayName) => {
    setError(null);
    try {
      if (!user || !user.isAnonymous) {
        return signUp(email, password, displayName);
      }

      const credential = EmailAuthProvider.credential(email, password);
      const result = await linkWithCredential(user, credential);

      if (displayName) {
        await updateProfile(result.user, { displayName });
      }

      const newUserData = {
        email: result.user.email,
        displayName: displayName || email.split('@')[0],
        photoURL: null,
        subscription_tier: 'basic',
        subscription_expires: null,
        role: 'consumer',
        created_at: serverTimestamp(),
        last_login: serverTimestamp()
      };
      await setDoc(doc(db, 'users', result.user.uid), newUserData);

      return result.user;
    } catch (error) {
      if (error.code === 'auth/credential-already-in-use' || error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Please sign in instead.');
      } else {
        setError(error.message);
      }
      throw error;
    }
  };

  // Sign out (auto re-signs in anonymously via onAuthStateChanged)
  const logOut = async () => {
    setError(null);
    try {
      await signOut(auth);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Update user data
  const updateUserData = async (data) => {
    if (!user) return;

    try {
      await setDoc(doc(db, 'users', user.uid), data, { merge: true });

      // Update local state
      setUserData(prev => ({ ...prev, ...data }));

      return true;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const value = {
    user,
    userData,
    loading,
    error,
    isAnonymous,
    signIn,
    signInWithGoogle,
    signUp,
    logOut,
    updateUserData,
    linkWithGoogle,
    linkWithEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
